import type { FieldValues, Path } from 'react-hook-form';
import {
	cn,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	InputBox,
} from '@lqa-frontend/ui-system';
import type {
	ILabelProps,
	InputSize,
	InputVariant,
} from '@lqa-frontend/ui-system';
import { omit } from 'lodash';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';

function InputFormField<T extends FieldValues>({
	className,
	wrapperClassName,
	variant,
	type,
	info,
	link,
	isRequired,
	isOptional,
	maxCount,
	...props
}: IInputFormFieldProps<T>) {
	const form = useFormContext();

	return (
		<FormField
			control={form.control}
			name={props.name}
			render={({ field }) => (
				<FormItem className={cn('w-full', props.itemClassName)}>
					{props.label ? (
						<FormLabel
							info={info}
							link={link}
							isRequired={isRequired}
							isOptional={isOptional}
							maxCount={maxCount}
							charCount={
								maxCount !== undefined
									? String(form.getValues(props.name) ?? '').length
									: undefined
							}
						>
							{props.label}
						</FormLabel>
					) : (
						''
					)}
					<FormControl>
						<InputBox
							variant={variant}
							{...{
								...field,
								...(type === 'number'
									? {
											onChange: (e) =>
												field.onChange(
													e.target.value === '' ? '' : Number(e.target.value)
												),
										}
									: {}),
								...omit(props, ['itemClassName']),
								className,
								wrapperClassName,
								value: field.value ?? '',
								type,
							}}
						/>
					</FormControl>
					{props.description ? (
						<FormDescription>{props.description}</FormDescription>
					) : (
						''
					)}
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}

interface IInputFormFieldProps<T extends FieldValues>
	extends
		Omit<React.ComponentProps<'input'>, 'size'>,
		Pick<
			ILabelProps,
			'info' | 'link' | 'isRequired' | 'isOptional' | 'maxCount'
		> {
	name: Path<T>;
	description?: string;
	label?: string | React.ReactNode;
	itemClassName?: string;
	wrapperClassName?: string;
	variant?: InputVariant;
	size?: InputSize;
}

export { InputFormField };
